import { api_fetch_throws } from "@/api/client.tsx"
import type { DeductionKind } from "@/api/deduction.tsx"

/** A deduction, repayment or invoice payment that names an order. */
export interface PoTransaction {
  id: string
  source: string
  kind: DeductionKind
  invoice_number: string
  /** In reporting currency (dollars). */
  amount: number
  date: string | null
  status: string | null
  category: string | null
}

export interface PurchaseOrderAllowance {
  kind: string | null
  code: string | null
  description: string | null
  percent: number | null
  amount: number | null
}

export interface PurchaseOrderLine {
  id: string
  line_number: number
  item_number: string | null
  upc: string | null
  buyer_item_number: string | null
  quantity: number
  uom: string | null
  unit_price: number | null
  case_pack: number | null
  request_date: string | null
  extended_price: number | null
  description: string | null
}

/** A customer's purchase order, read from the EDI document it mailed. */
export interface PurchaseOrder {
  id: string
  partner: string
  source: string | null
  customer_name: string
  po_number: string
  po_date: string | null
  requested_date: string | null
  requested_ship_date: string | null
  purpose: string | null
  order_type: string | null
  vendor_number: string | null
  ship_to_name: string | null
  ship_to_address: string | null
  bill_to: string | null
  fob: string | null
  terms_description: string | null
  terms_discount_percent: number | null
  terms_discount_days: number | null
  terms_net_days: number | null
  total_quantity: number | null
  total_amount: number | null
  allowances: PurchaseOrderAllowance[]
  s3_uri: string | null
  document_date: string
  line_count: number
  lead_days: number | null
  transactions: PoTransaction[]
  /** Present on the single-order and deduction views only. */
  lines?: PurchaseOrderLine[]
}

export interface PoItemCustomer {
  customer_name: string
  quantity: number
  amount: number
  min_price: number | null
  max_price: number | null
  latest_price: number | null
}

export interface PoItemSummary {
  item_number: string
  upc: string | null
  description: string | null
  quantity: number
  amount: number
  order_count: number
  customers: PoItemCustomer[]
}

export interface PurchaseOrdersResponse {
  from: string
  to: string
  orders: PurchaseOrder[]
  items: PoItemSummary[]
  earliest_po_date: string | null
}

export async function fetchPurchaseOrders(days: number): Promise<PurchaseOrdersResponse> {
  return await api_fetch_throws<PurchaseOrdersResponse>(`/purchase-order?days=${days}`)
}

export async function fetchPurchaseOrder(id: string): Promise<PurchaseOrder> {
  return await api_fetch_throws<PurchaseOrder>(`/purchase-order/${id}`)
}

export async function fetchPurchaseOrderCount(): Promise<{ count: number }> {
  return await api_fetch_throws<{ count: number }>(`/purchase-order/count`)
}
